import React from 'react';

import { Badge } from '../../../components/ui/badge';
import { Spinner } from '../../../components/ui/spinner';
import { PaginationControls } from '../../../components/ui/pagination';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import { cn } from 'lib/utils';
import type { FeedbackReport } from './types';
import { formatDateTime, truncateText } from './utils';

type FeedbackReportsTableProps = {
  reports: FeedbackReport[];
  loading: boolean;
  total: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  selectedReportId: number | null;
  onSelectReport: (id: number) => void;
  statusLabelMap: Map<string, string>;
  severityLabelMap: Map<string, string>;
};

export const FeedbackReportsTable: React.FC<FeedbackReportsTableProps> = ({
  reports,
  loading,
  total,
  page,
  pageSize,
  onPageChange,
  selectedReportId,
  onSelectReport,
  statusLabelMap,
  severityLabelMap,
}) => {
  return (
    <div className="flex w-full flex-col gap-4">
      <div className="flex items-center justify-between text-sm text-muted-foreground">
        <span>{total} total</span>
        {loading ? <Spinner size="sm" /> : null}
      </div>
      <div className="space-y-2">
        <div className="max-h-[420px] overflow-y-auto rounded-3xl border border-border/40">
          <Table>
            <TableHeader className="bg-white/80">
              <TableRow>
                <TableHead className="w-16">Report</TableHead>
                <TableHead className="w-[8.4375rem]">Status</TableHead>
                <TableHead>Severity</TableHead>
                <TableHead>Feedback</TableHead>
                <TableHead className="w-[11.875rem]">Created</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {reports.length === 0 && !loading ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-sm text-muted-foreground">
                    No bug reports found.
                  </TableCell>
                </TableRow>
              ) : (
                reports.map((report) => (
                  <TableRow
                    key={report.id}
                    className={cn('cursor-pointer', selectedReportId === report.id && 'bg-white/70')}
                    onClick={() => onSelectReport(report.id)}
                  >
                    <TableCell className="font-semibold">#{report.id}</TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {statusLabelMap.get(report.status) ?? report.status_display ?? report.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {report.severity ? (
                        <Badge variant="secondary">
                          {severityLabelMap.get(report.severity) ??
                            report.severity_display ??
                            report.severity}
                        </Badge>
                      ) : null}
                    </TableCell>
                    <TableCell className="max-w-xs text-sm text-muted-foreground">
                      {truncateText(report.feedback_text)}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDateTime(report.created_at)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
      <PaginationControls page={page} pageSize={pageSize} totalCount={total} onPageChange={onPageChange} />
    </div>
  );
};
